import {
	Box,
	Flex,
	FormControl,
	Input,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	Stack,
	Text,
} from "@chakra-ui/react"
import { ErrorMessage, Formik } from "formik"
import moment from "moment"
import { ComponentProps, FC } from "react"
import { ErrorMessageField } from "src/components/ui"
import { DrawerForm } from "src/components/ui/DrawerForm"
import { InputLabel } from "src/components/ui/InputLabel"
import { IHallBookingBill } from "src/domain/entities"
import { FormikOnSubmit } from "src/utils/types"
import { IHallBookingBillUpdateFormFields } from "."
import {
	calculateUtilityAmount,
	DEFAULT_ELECTRICITY_PRICE_PER_UNIT,
	DEFAULT_GAS_PRICE_PER_UNIT,
	derivePricePerUnit,
} from "../hallBookingBillUtilityCharge"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	hallBookingBill: IHallBookingBill
	handleSubmit: FormikOnSubmit<IHallBookingBillUpdateFormFields>
}

export const HallBookingBillUpdateDrawerFormView: FC<Props> = ({
	hallBookingBill,
	handleSubmit,
	...rest
}) => (
	<Formik<IHallBookingBillUpdateFormFields>
		initialValues={{
			billNo: hallBookingBill.billNo ?? "",
			date: moment(hallBookingBill.date).format("YYYY-MM-DD"),
			electricity: {
				previousReading: hallBookingBill.electricityPreviousReading ?? 0,
				currentReading: hallBookingBill.electricityCurrentReading ?? 0,
				pricePerUnit: derivePricePerUnit(
					hallBookingBill.electricityPreviousReading ?? 0,
					hallBookingBill.electricityCurrentReading ?? 0,
					hallBookingBill.electricityBillAmount ?? 0,
					DEFAULT_ELECTRICITY_PRICE_PER_UNIT,
				),
				amount: hallBookingBill.electricityBillAmount ?? 0,
			},
			gas: {
				previousReading: hallBookingBill.gasPreviousReading ?? 0,
				currentReading: hallBookingBill.gasCurrentReading ?? 0,
				pricePerUnit: derivePricePerUnit(
					hallBookingBill.gasPreviousReading ?? 0,
					hallBookingBill.gasCurrentReading ?? 0,
					hallBookingBill.gasBillAmount ?? 0,
					DEFAULT_GAS_PRICE_PER_UNIT,
				),
				amount: hallBookingBill.gasBillAmount ?? 0,
			},
		}}
		onSubmit={handleSubmit}
		enableReinitialize
	>
		{({ values, isSubmitting, handleChange, setFieldValue }) => (
			<DrawerForm
				size="lg"
				headerLabel="Update Hall Booking Bill"
				submitLabel="Save"
				isSubmitting={isSubmitting}
				{...rest}
			>
				<Stack marginX={"auto"} spacing={4}>
					<Flex gap={4}>
						{/* Bill Number */}
						<FormControl>
							<InputLabel label="Bill Number" />
							<Input
								name="billNo"
								placeholder="VVLP/001/2026-27"
								value={values.billNo}
								onChange={handleChange}
								required
							/>
							<ErrorMessage
								component={ErrorMessageField}
								name="billNo"
							/>
						</FormControl>

						{/* Date */}
						<FormControl>
							<InputLabel label="Date" />
							<Input
								name="date"
								type="date"
								value={values.date}
								onChange={handleChange}
								required
							/>
						</FormControl>
					</Flex>

					{/* Electricity Section */}
					<Box borderWidth={1} borderRadius="md" p={4}>
						<Text fontWeight="bold" mb={3}>
							Electricity
						</Text>
						<Flex gap={2} flexWrap="wrap">
							<FormControl flex="1" minW="140px">
								<InputLabel label="Previous Reading" />
								<NumberInput
									value={values.electricity.previousReading}
									onChange={(_, value) => {
										setFieldValue(
											"electricity.previousReading",
											value,
										)
										setFieldValue(
											"electricity.amount",
											calculateUtilityAmount(
												value,
												values.electricity.currentReading,
												values.electricity.pricePerUnit,
											),
										)
									}}
								>
									<NumberInputField />
									<NumberInputStepper>
										<NumberIncrementStepper />
										<NumberDecrementStepper />
									</NumberInputStepper>
								</NumberInput>
							</FormControl>
							<FormControl flex="1" minW="140px">
								<InputLabel label="Current Reading" />
								<NumberInput
									value={values.electricity.currentReading}
									onChange={(_, value) => {
										setFieldValue(
											"electricity.currentReading",
											value,
										)
										setFieldValue(
											"electricity.amount",
											calculateUtilityAmount(
												values.electricity.previousReading,
												value,
												values.electricity.pricePerUnit,
											),
										)
									}}
								>
									<NumberInputField />
									<NumberInputStepper>
										<NumberIncrementStepper />
										<NumberDecrementStepper />
									</NumberInputStepper>
								</NumberInput>
							</FormControl>
							<FormControl flex="1" minW="140px">
								<InputLabel label="Price per Unit" />
								<NumberInput
									value={values.electricity.pricePerUnit}
									onChange={(_, value) => {
										setFieldValue(
											"electricity.pricePerUnit",
											value,
										)
										setFieldValue(
											"electricity.amount",
											calculateUtilityAmount(
												values.electricity.previousReading,
												values.electricity.currentReading,
												value,
											),
										)
									}}
								>
									<NumberInputField />
									<NumberInputStepper>
										<NumberIncrementStepper />
										<NumberDecrementStepper />
									</NumberInputStepper>
								</NumberInput>
							</FormControl>
							<FormControl flex="1" minW="140px">
								<InputLabel label="Amount" />
								<NumberInput
									value={values.electricity.amount}
									onChange={(_, value) =>
										setFieldValue("electricity.amount", value)
									}
									isReadOnly
								>
									<NumberInputField />
								</NumberInput>
							</FormControl>
						</Flex>
					</Box>

					{/* Gas Section */}
					<Box borderWidth={1} borderRadius="md" p={4}>
						<Text fontWeight="bold" mb={3}>
							Gas
						</Text>
						<Flex gap={2} flexWrap="wrap">
							<FormControl flex="1" minW="140px">
								<InputLabel label="Previous Reading" />
								<NumberInput
									value={values.gas.previousReading}
									onChange={(_, value) => {
										setFieldValue(
											"gas.previousReading",
											value,
										)
										setFieldValue(
											"gas.amount",
											calculateUtilityAmount(
												value,
												values.gas.currentReading,
												values.gas.pricePerUnit,
											),
										)
									}}
								>
									<NumberInputField />
									<NumberInputStepper>
										<NumberIncrementStepper />
										<NumberDecrementStepper />
									</NumberInputStepper>
								</NumberInput>
							</FormControl>
							<FormControl flex="1" minW="140px">
								<InputLabel label="Current Reading" />
								<NumberInput
									value={values.gas.currentReading}
									onChange={(_, value) => {
										setFieldValue("gas.currentReading", value)
										setFieldValue(
											"gas.amount",
											calculateUtilityAmount(
												values.gas.previousReading,
												value,
												values.gas.pricePerUnit,
											),
										)
									}}
								>
									<NumberInputField />
									<NumberInputStepper>
										<NumberIncrementStepper />
										<NumberDecrementStepper />
									</NumberInputStepper>
								</NumberInput>
							</FormControl>
							<FormControl flex="1" minW="140px">
								<InputLabel label="Price per Unit" />
								<NumberInput
									value={values.gas.pricePerUnit}
									onChange={(_, value) => {
										setFieldValue("gas.pricePerUnit", value)
										setFieldValue(
											"gas.amount",
											calculateUtilityAmount(
												values.gas.previousReading,
												values.gas.currentReading,
												value,
											),
										)
									}}
								>
									<NumberInputField />
									<NumberInputStepper>
										<NumberIncrementStepper />
										<NumberDecrementStepper />
									</NumberInputStepper>
								</NumberInput>
							</FormControl>
							<FormControl flex="1" minW="140px">
								<InputLabel label="Amount" />
								<NumberInput
									value={values.gas.amount}
									onChange={(_, value) =>
										setFieldValue("gas.amount", value)
									}
									isReadOnly
								>
									<NumberInputField />
								</NumberInput>
							</FormControl>
						</Flex>
					</Box>
				</Stack>
			</DrawerForm>
		)}
	</Formik>
)
